import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

const CURRICULUM_FILE_FIELD = 'curriculum_file'
const DISCIPLINE_CODE_FIELD = 'discipline_code'

interface CurriculumImportApiOptions {
  disciplineCode: DisciplineCode
  file: File
  /** 0–100. */
  onProgress?: (percent: number) => void
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST /admin-api/curriculum/import` — multipart CSV upload for one discipline; appends a new
 * versioned curriculum set. Mirrors the media-upload hook: no global loader (the caller owns
 * progress) and no `Content-Type` header, so axios sets the multipart boundary itself.
 */
export const useCurriculumImportApi = async (
  options: CurriculumImportApiOptions,
): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.curriculum.import')

  options.onStart?.()

  try {
    if (!url) {
      const message = 'Unexpected error. Invalid request URL.'
      toast.error(message)
      options.onError?.(message)
      return false
    }

    const formData = new FormData()
    formData.append(CURRICULUM_FILE_FIELD, options.file)
    formData.append(DISCIPLINE_CODE_FIELD, options.disciplineCode)

    const { data } = await $axios.post<ApiResponse>(url, formData, {
      onUploadProgress: (event) => {
        if (!event.total) return
        options.onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return false
    }

    toast.success(data.message)
    options.onSuccess?.()
    return true
  } catch (err: unknown) {
    const message = getAxiosErrorMessage(err)
    toast.error(message)
    options.onError?.(message)
    return false
  } finally {
    options.onFinish?.()
  }
}
